import React, { useEffect } from "react"
import { Link } from "react-router-dom"
import RiskScore from "./charts/RiskScore"
import SecretsDistribution from "./charts/SecretsDistribution"
import HeatMap from "./charts/HeatMap"
import Insights from "./charts/Insights"
 import DeveloperRiskTable from "./Tables/DeveloperRiskTable"
import DailyFindings from "./charts/DailyFindings"
import { useSecurityStore } from "../../stores/SecurityStore"
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator
} from '../../components/ui/breadcrumb'
import { useDashboardTour } from "../../components/Tour/DashboardTour"


// Debug component to show store state
const DebugPanel = () => {
  const repositories = useSecurityStore((state) => state.repositories);
  const riskScores = useSecurityStore((state) => state.riskScores);
  const isLoading = useSecurityStore((state) => state.isLoading);
  const riskScoresLoading = useSecurityStore((state) => state.riskScoresLoading);
  const error = useSecurityStore((state) => state.error);
  const riskScoresError = useSecurityStore((state) => state.riskScoresError);
  const getAggregatedCounts = useSecurityStore((state) => state.getAggregatedCounts);
  const getTotalRiskScore = useSecurityStore((state) => state.getTotalRiskScore);
  const getDailyRiskScores = useSecurityStore((state) => state.getDailyRiskScores);

  const aggregatedCounts = getAggregatedCounts();
  const totalRiskScore = getTotalRiskScore();
  const dailyRiskScores = getDailyRiskScores();

  return (
    <div className="fixed top-0 right-0 w-[300px] md:max-h-screen bg-white border border-gray-300 p-2.5 text-xs">
      <h3>Debug Panel</h3>
      <div><strong>Loading States:</strong></div>
      <div>isLoading: {isLoading ? 'true' : 'false'}</div>
      <div>riskScoresLoading: {riskScoresLoading ? 'true' : 'false'}</div>
      
      <div className="mt-2.5"><strong>Errors:</strong></div>
      <div>error: {error || 'none'}</div>
      <div>riskScoresError: {riskScoresError || 'none'}</div>
      
      <div className="mt-2.5"><strong>Repositories:</strong></div>
      <div>Count: {repositories.length}</div>
      <pre className="text-[10px] bg-gray-100 p-1.5">
        {JSON.stringify(repositories, null, 2)}
      </pre>
      
      <div className="mt-2.5"><strong>Risk Scores:</strong></div>
      <div>Total: {totalRiskScore}</div>
      <div>Daily count: {dailyRiskScores.length}</div>
      <pre className="text-[10px] bg-gray-100 p-1.5">
        {JSON.stringify(riskScores, null, 2)}
      </pre>
      
      <div className="mt-2.5"><strong>Aggregated Counts:</strong></div>
      <pre className="text-[10px] bg-gray-100 p-1.5">
        {JSON.stringify(aggregatedCounts, null, 2)}
      </pre>
    </div>
  );
};

const DashboardPage = () => {
  const showDebug = false;
  
  const { loadInitialData, cleanup } = useSecurityStore();
  
  // Initialize tour with auto-start for first-time users
  useDashboardTour(true);

  useEffect(() => {
    loadInitialData().then(() => {
    }).catch(error => {
      console.error('Dashboard: Data load failed', error);
    });

    // Optional cleanup on unmount
    return () => {
      cleanup(); // stops polling cleanly
    };
  }, [loadInitialData, cleanup]);

  return (
    <div className="bg-white m-3 sm:m-4 md:m-6 mt-3 overflow-x-hidden max-w-full">
      <div className="mx-auto">
        {/* Debug Panel */}
        {showDebug && <DebugPanel />}

        {/* Breadcrumb + Page Title */}
        <div className="mb-8 space-y-2">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/">Home</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>Dashboard</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>

          <h1 className="text-black text-2xl md:text-3xl font-bold" data-tour="welcome-title">Welcome to StackGuard</h1>
        </div>

        <div className="flex flex-col gap-4 md:gap-6">

          {/* Analytics Overview Section */}
          <section>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
              {/* Left Chart */}
              <div className="col-span-1 lg:col-span-1 order-1" data-tour="risk-score">
                <RiskScore />
              </div>

              {/* Right Chart */}
              <div className="col-span-1 lg:col-span-1 order-2 lg:order-3" data-tour="secrets-distribution">
                <SecretsDistribution />
              </div>

              {/* Center Chart */}
              <div className="col-span-2 lg:col-span-2 order-3 lg:order-2" data-tour="daily-findings">
                <DailyFindings />
              </div>
            </div>
          </section>

          {/* Trends Section */}
          <section data-tour="heatmap" >
            <HeatMap />
          </section>

          {/* Analysis Section */}
          <section data-tour="policy-insights">
            <Insights />
          </section>
        </div>
      </div>
    </div>
  )
}

export default DashboardPage